import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, Eye, Play, Youtube } from "lucide-react";
import { api } from "@/lib/api";
import { SOCIALS } from "@/lib/social";
import { formatViews, platformLabel, type Video, type VideoSort, type VideosResponse } from "@/lib/videos";
import { FOLLOW } from "../../ConceptBits";
import { CLUB, EASE, HEART, Head, Ink, VIEW } from "../bits";
import { LiveDates } from "../LiveDates";
import { Blue2Page, Part } from "../Page";
import "../SectionsA.css";
import "./Videos.css";

/* The blue-2 videos page: the newest video up top, the live schedule, then
   every video as taped polaroids with picks/sort filters and "load more". */

const PAGE_SIZE = 8;
const ALL_LIMIT = 48; // the API's max page size, used for the picks filter
const TAPES = ["soft", "extra", "pop", "strong"] as const;
const TILT = [-2.5, 1.8, -1.2, 2.4];
const SUBSCRIBE = `${SOCIALS.youtube.url}?sub_confirmation=1`;

type Filter = "all" | "picks";

const shortDate = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const isShort = (v: Video) => /\/shorts\//.test(v.externalUrl);
const kind = (v: Video) => (isShort(v) ? "Short" : platformLabel(v.platform));
/** YouTube's hqdefault thumbnail letterboxes widescreen videos; crop the bars. */
const thumbClass = (v: Video) => (v.platform === "youtube" && !isShort(v) ? "bv-wide" : undefined);

/** Newest video, shown big in the page header. */
function Featured() {
  const [video, setVideo] = useState<Video | null | undefined>(undefined);
  useEffect(() => {
    api.get<VideosResponse>("/api/videos?page=1&limit=1&sort=recent").then(r => setVideo(r.videos[0] ?? null)).catch(() => setVideo(null));
  }, []);
  if (video === null) return null;
  return <motion.a className="bv-feature" href={video?.externalUrl ?? SOCIALS.youtube.url} target="_blank" rel="noreferrer"
    aria-label={video ? `Watch my newest video: ${video.title}` : "Loading my newest video"}
    initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
    <span className="bv-feature-img">
      {video?.thumbnailUrl ? <img className={thumbClass(video)} src={video.thumbnailUrl} alt="" /> : <span className="bv-skeleton" />}
      <b><Play size={26} fill="currentColor" aria-hidden="true" /></b>
    </span>
    <span className="bv-feature-cap">{video?.title ?? " "}</span>
    <span className="bv-feature-sticker" aria-hidden="true">newest!</span>
  </motion.a>;
}

function Header() {
  return <div className="bv-head">
    <div className="bv-head-copy">
      <motion.p className="bv-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Watch along</motion.p>
      <h1 className="bv-title">
        <motion.span className="bv-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.35, duration: 0.5, ease: EASE }}>what I’m</motion.span>
        <span className="bv-title-word" aria-label="making">
          {"making".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <motion.p className="bv-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.45, ease: EASE }}>
        Sewing projects, little hacks, fabric hauls, and the occasional creative detour. Pull up a chair and watch along.
      </motion.p>
      <motion.div className="bv-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.45, ease: EASE }}>
        <a href={SUBSCRIBE} target="_blank" rel="noreferrer" className="b2-btn b2-btn-yellow"><Youtube size={18} aria-hidden="true" /> Subscribe on YouTube</a>
        <a href="#all-videos" className="b2-btn b2-btn-ghost">Browse them all</a>
      </motion.div>
    </div>
    <Featured />
  </div>;
}

/** The live schedule on the teal club band. */
function Lives() {
  return <section className="b2-sec bv-lives" aria-labelledby="bv-lives-title">
    <div className="b2-wrap bv-lives-grid">
      <div>
        <p className="b2-kicker bv-lives-kicker">Sew with me in real time</p>
        <Head id="bv-lives-title" hand="catch me" word="live" tone="pink" />
        <p className="b2-lede">Most Tuesdays I’m live on YouTube, free, at 7 PM Mountain. Club members also get a two-night class in week two, with every replay.</p>
        <div className="bv-lives-actions">
          <a href={`${SOCIALS.youtube.url}/live`} target="_blank" rel="noreferrer" className="b2-btn b2-btn-yellow"><Play size={16} fill="currentColor" aria-hidden="true" /> Watch live on YouTube</a>
          <Link to={CLUB} className="b2-btn b2-btn-ghost">About the club</Link>
        </div>
      </div>
      <LiveDates />
    </div>
  </section>;
}

function VideoCard({ v, i }: { v: Video; i: number }) {
  const n = i % 4;
  return <motion.li layout initial={{ opacity: 0, y: 40, rotate: TILT[n] + 5 }} whileInView={{ opacity: 1, y: 0, rotate: TILT[n] }} viewport={VIEW}
    transition={{ delay: (i % PAGE_SIZE % 4) * 0.08, type: "spring", stiffness: 140, damping: 16 }}>
    <a className="b2-snap bv-card" href={v.externalUrl} target="_blank" rel="noreferrer">
      <i className={`b2-snap-tape b2-ktape-${TAPES[n]}`} aria-hidden="true" />
      <span className="b2-snap-img">
        {v.thumbnailUrl ? <img className={thumbClass(v)} src={v.thumbnailUrl} alt="" loading="lazy" /> : <span className="bv-noimg"><Play size={30} aria-hidden="true" /></span>}
        <b><Play size={18} fill="currentColor" aria-hidden="true" /></b>
        {v.featured && <em className="bv-pick">a fave ♥</em>}
      </span>
      <span className="bv-card-meta">
        <span className="b2-snap-tag">{kind(v)}{shortDate(v.publishedAt) && ` · ${shortDate(v.publishedAt)}`}</span>
        {v.viewCount > 0 && <span className="bv-views"><Eye size={13} aria-hidden="true" /> {formatViews(v.viewCount)}</span>}
      </span>
      <span className="b2-snap-title bv-card-title">{v.title}</span>
    </a>
  </motion.li>;
}

function AllVideos() {
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<VideoSort>("recent");
  const [videos, setVideos] = useState<Video[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<"loading" | "more" | "done" | "error">("loading");

  // A new filter or sort starts over at page one.
  useEffect(() => {
    let live = true;
    setStatus("loading");
    const limit = filter === "picks" ? ALL_LIMIT : PAGE_SIZE;
    api.get<VideosResponse>(`/api/videos?page=1&limit=${limit}&sort=${sort}`)
      .then(r => {
        if (!live) return;
        setVideos(filter === "picks" ? r.videos.filter(v => v.featured) : r.videos);
        setHasMore(filter === "all" && r.hasMore);
        setPage(1);
        setStatus("done");
      })
      .catch(() => live && setStatus("error"));
    return () => { live = false; };
  }, [filter, sort]);

  function loadMore() {
    setStatus("more");
    api.get<VideosResponse>(`/api/videos?page=${page + 1}&limit=${PAGE_SIZE}&sort=${sort}`)
      .then(r => {
        setVideos(prev => [...prev, ...r.videos.filter(v => !prev.some(p => p.id === v.id))]);
        setHasMore(r.hasMore);
        setPage(p => p + 1);
        setStatus("done");
      })
      .catch(() => setStatus("error"));
  }

  const chip = (active: boolean) => `bv-chip${active ? " is-on" : ""}`;
  return <section id="all-videos" className="b2-sec bv-all" aria-labelledby="bv-all-title">
    <div className="b2-wrap">
      <div className="b2-row bv-all-row">
        <Head id="bv-all-title" hand="every last" word="video" tone="blue" />
        <div className="bv-filters">
          <div className="bv-chips" role="group" aria-label="Show">
            <button type="button" className={chip(filter === "all")} aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All videos</button>
            <button type="button" className={chip(filter === "picks")} aria-pressed={filter === "picks"} onClick={() => setFilter("picks")}>My faves ♥</button>
          </div>
          <div className="bv-chips" role="group" aria-label="Sort">
            <button type="button" className={chip(sort === "recent")} aria-pressed={sort === "recent"} onClick={() => setSort("recent")}>Newest</button>
            <button type="button" className={chip(sort === "views")} aria-pressed={sort === "views"} onClick={() => setSort("views")}>Most watched</button>
          </div>
        </div>
      </div>

      {status === "loading" && <ul className="bv-grid" aria-busy="true" aria-label="Loading videos">
        {Array.from({ length: 4 }, (_, i) => <li key={i}><span className="b2-snap bv-card bv-card-ghost"><span className="b2-snap-img"><span className="bv-skeleton" /></span><span className="bv-line" /><span className="bv-line bv-line-short" /></span></li>)}
      </ul>}

      {status === "error" && videos.length === 0 && <div className="bv-empty" role="status">
        <p className="b2-note">The videos are hiding right now.</p>
        <p>Catch every one on my channel instead.</p>
        <a href={SOCIALS.youtube.url} target="_blank" rel="noreferrer" className="b2-btn b2-btn-blue">Go to YouTube <ArrowRight size={16} aria-hidden="true" /></a>
      </div>}

      {status !== "loading" && status !== "error" && videos.length === 0 && <div className="bv-empty" role="status">
        <p className="b2-note">{filter === "picks" ? "No faves picked yet." : "No videos yet."}</p>
        {filter === "picks" && <button type="button" className="b2-btn b2-btn-blue" onClick={() => setFilter("all")}>Show all videos</button>}
      </div>}

      {status !== "loading" && videos.length > 0 && <ul className="bv-grid">
        {videos.map((v, i) => <VideoCard key={v.id} v={v} i={i} />)}
      </ul>}

      <div className="bv-more" aria-live="polite">
        {hasMore && status !== "loading" && <button type="button" className="b2-btn b2-btn-blue" onClick={loadMore} disabled={status === "more"}>
          {status === "more" ? "Loading…" : <>Load more videos <ArrowRight size={16} aria-hidden="true" /></>}
        </button>}
        {status === "error" && videos.length > 0 && <p className="bv-more-err">That didn’t load. Try again in a minute?</p>}
        {!hasMore && status === "done" && videos.length > 0 && <p className="b2-note bv-more-end">That’s all of them here. <a href={SOCIALS.youtube.url} target="_blank" rel="noreferrer">More on YouTube →</a></p>}
      </div>
    </div>
  </section>;
}

/** Golden band: subscribe card + club card. */
function Follow() {
  return <section className="b2-sec bv-follow" aria-labelledby="bv-follow-title">
    <div className="b2-wrap">
      <Head id="bv-follow-title" hand="never miss" word="a stitch" tone="blue" className="b2-head-center" />
      <div className="bv-follow-grid">
        <motion.div className="bv-sub" initial={{ opacity: 0, y: 40, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -1.5 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
          <span className="bv-sub-photo"><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
          <div>
            <p className="b2-snap-tag">New videos every week</p>
            <h3>Subscribe and sew along</h3>
            <p className="bv-sub-text">Cosmo approves every video before it goes up. You’re welcome.</p>
            <a href={SUBSCRIBE} target="_blank" rel="noreferrer" className="b2-btn bv-sub-btn"><Youtube size={18} aria-hidden="true" /> Subscribe</a>
            <ul className="bv-sub-social" aria-label="Follow along">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
          </div>
        </motion.div>
        <motion.div className="bv-club" initial={{ opacity: 0, y: 40, rotate: -3 }} whileInView={{ opacity: 1, y: 0, rotate: 1.5 }} viewport={VIEW} transition={{ delay: 0.12, type: "spring", stiffness: 120, damping: 16 }}>
          <p className="b2-snap-tag">Loose Threads Club</p>
          <h3>Want to sew <span>with</span> me, not just watch?</h3>
          <p>Two live classes a month, every replay, and the nicest people on the internet. $25 a month, cancel anytime.</p>
          <Link to={CLUB} className="b2-btn b2-btn-yellow">Come sew with me <ArrowRight size={16} aria-hidden="true" /></Link>
          <svg className="bv-club-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.8} width={3.4} /></svg>
        </motion.div>
      </div>
    </div>
  </section>;
}

export function Blue2Videos() {
  return <Blue2Page current="videos" tone="teal" header={<Header />}>
    {/* Periwinkle band, so the live schedule doesn’t run into the teal header. */}
    <Part p="making"><Lives /></Part>
    <Part p="making"><AllVideos /></Part>
    <Part p="loop"><Follow /></Part>
  </Blue2Page>;
}
